import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../../../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import EggIcon from '@mui/icons-material/Egg';
import SetMealIcon from '@mui/icons-material/SetMeal';
import DinnerDiningIcon from '@mui/icons-material/DinnerDining';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import RiceBowlIcon from '@mui/icons-material/RiceBowl';
import GrassIcon from '@mui/icons-material/Grass';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import KebabDiningIcon from '@mui/icons-material/KebabDining';
import EggAltIcon from '@mui/icons-material/EggAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import CoffeeIcon from '@mui/icons-material/Coffee';
import CookieIcon from '@mui/icons-material/Cookie';

const MenuContainer = styled(Paper)({
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    border: '1px solid #d4b483',
    fontFamily: 'Arial, sans-serif',
    '@media print': {
        boxShadow: 'none',
        border: '1px solid #000'
    }
});

const HeaderBox = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1e3a5f',
    color: 'white',
    padding: '8px',
    textAlign: 'center',
    borderBottom: '3px solid #d4b483'
});

const Title = styled(Typography)({
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    fontSize: '15px',
    letterSpacing: '2px',
    marginBottom: '5px'
});

const PropertyInfo = styled(Box)({
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '30px',
    padding: '4px 18px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '5px'
});

const WeekBadge = styled(Box)({
    backgroundColor: '#d4b483',
    color: '#1e3a5f',
    padding: '4px 16px',
    borderRadius: '4px',
    display: 'inline-block',
    fontWeight: 'bold',
    marginTop: '12px',
    fontSize: '14px'
});

const IconBar = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    gap: '25px',
    marginTop: '15px',
    color: '#d4b483'
});

const SubHeader = styled(Box)({
    backgroundColor: '#f5ede1',
    padding: '15px',
    textAlign: 'center',
    borderBottom: '1px solid #d4b483'
});

const SubHeaderTitle = styled(Typography)({
    color: '#1e3a5f',
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '5px'
});

const MealStats = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    flexWrap: 'wrap',
    marginBottom: '8px'
});

const StatItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px'
});

const NoteText = styled(Typography)({
    fontSize: '13px',
    color: '#666',
    fontStyle: 'italic'
});

const StyledTable = styled(Table)({
    borderCollapse: 'collapse',
    '& td, & th': {
        border: '1px solid #d4b483',
        padding: '5px',
        verticalAlign: 'top'
    }
});

const DayCell = styled(TableCell)({
    backgroundColor: '#f5ede1',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#1e3a5f',
    fontSize: '16px',
    borderBottom: '2px solid #d4b483 !important',
    padding: '10px !important'
});

const MealTypeCell = styled(TableCell)(({ mealtype }) => ({
    backgroundColor: mealtype === 'BREAKFAST' ? '#e6f3e6' :
                    mealtype === 'LUNCH' ? '#ffe6e6' :
                    mealtype === 'SNACKS' ? '#fff4e6' :
                    mealtype === 'DINNER' ? '#e6f0fa' : '#f5ede1',
    fontWeight: 'bold',
    width: '100px',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontSize: '14px',
    borderRight: '2px solid #d4b483 !important'
}));

const MenuItem = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    marginBottom: '4px',
    fontSize: '12px',
    lineHeight: '1.3',
    '& svg': {
        fontSize: '14px',
        marginTop: '2px',
        flexShrink: 0
    }
});

const FoodName = styled(Typography)({
    fontSize: '12px',
    lineHeight: '1.3',
    color: '#333'
});

const FooterNote = styled(Box)({
    backgroundColor: '#f5ede1',
    padding: '15px',
    margin: '20px 15px',
    border: '1px dashed #d4b483',
    textAlign: 'center',
    fontSize: '13px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px'
});

const FooterText = styled(Box)({
    textAlign: 'center',
    padding: '10px',
    fontSize: '11px',
    color: '#999'
});

const WeeklyPrint = ({ globalProperty }) => {
    const [daysData, setDaysData] = useState([]);
    const [mealTypes, setMealTypes] = useState(['BREAKFAST', 'LUNCH', 'SNACKS', 'DINNER']);
    const [propertyName, setPropertyName] = useState("");
    const [weekNumber, setWeekNumber] = useState("");
    const [weekDates, setWeekDates] = useState({ start: '', end: '' });

    useEffect(() => {
        fetchWeeklyMenu();
        calculateWeekDates();
    }, [globalProperty]);

    const calculateWeekDates = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + 1);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        setWeekDates({
            start: startOfWeek.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
            end: endOfWeek.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
        });
        
        const weekNum = getWeekNumber(today);
        setWeekNumber(weekNum);
    };

    const getWeekNumber = (date) => {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    };

    const fetchWeeklyMenu = async () => {
        try {
            const response = await axiosInstance.post('MstFood/GetMstFoodWeekCalDashBoard', {
                start: 1,
                PageSize: 100,
                SearchText: globalProperty || "10006"
            });

            if (response.data.Status === 0) {
                const grouped = {};
                const data = response.data.Data[0];
                setPropertyName(data?.PropName || "Clanbridge Living");
                
                const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

                daysOrder.forEach(day => {
                    grouped[day] = {};
                });

                response.data.Data.forEach(item => {
                    const day = item.DayNm || 'Unknown Day';
                    if (!grouped[day]) {
                        grouped[day] = {};
                    }
                    const meal = item.MealTypeName?.toLowerCase() || 'other';
                    if (!grouped[day][meal]) {
                        grouped[day][meal] = [];
                    }
                    if (item.FoodName) {
                        grouped[day][meal].push(item.FoodName.trim());
                    }
                });

                const formattedDays = daysOrder
                    .filter(day => Object.keys(grouped[day]).length > 0)
                    .map((dayName) => ({
                        day: dayName,
                        breakfast: grouped[dayName]?.breakfast || [],
                        lunch: grouped[dayName]?.lunch || [],
                        snacks: grouped[dayName]?.snacks || [],
                        dinner: grouped[dayName]?.dinner || []
                    }));

                setDaysData(formattedDays);
            }
        } catch (error) {
            toast.error("Error while fetching weekly menu.");
        }
    };

    const getMealIcon = (mealType, foodName) => {
        const foodLower = foodName?.toLowerCase() || '';
        
        if (foodLower.includes('egg') || foodLower.includes('anda')) {
            return <EggAltIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('chicken')) {
            return <KebabDiningIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('rice') || foodLower.includes('pulao') || foodLower.includes('biryani')) {
            return <RiceBowlIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('roti') || foodLower.includes('paratha') || foodLower.includes('bread')) {
            return <BakeryDiningIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('dal') || foodLower.includes('curry') || foodLower.includes('masala')) {
            return <SetMealIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('soup')) {
            return <SoupKitchenIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('salad') || foodLower.includes('raita')) {
            return <GrassIcon sx={{ color: '#d4b483' }} />;
        } else if (foodLower.includes('tea') || foodLower.includes('coffee') || foodLower.includes('milk')) {
            return <LocalDrinkIcon sx={{ color: '#d4b483' }} />;
        } else if (mealType === 'BREAKFAST') {
            return <CoffeeIcon sx={{ color: '#d4b483' }} />;
        } else if (mealType === 'DINNER') {
            return <DinnerDiningIcon sx={{ color: '#d4b483' }} />;
        } else if (mealType === 'LUNCH') {
            return <LunchDiningIcon sx={{ color: '#d4b483' }} />;
        } else if (mealType === 'SNACKS') {
            return <CookieIcon sx={{ color: '#d4b483' }} />;
        }
        return <RestaurantMenuIcon sx={{ color: '#d4b483' }} />;
    };

    return (
        <MenuContainer>
            <ToastContainer style={{ '@media print': { display: 'none' } }} />
            
            <HeaderBox>
                <Title>CLANBRIDGE LIVING MENU</Title>
                <PropertyInfo>
                    <LocationOnIcon fontSize="small" />
                    <Typography variant="body2">{propertyName}</Typography>
                </PropertyInfo>
       <MealStats>
                    <StatItem>
                        <KebabDiningIcon sx={{ color: '#1e3a5f' }} />
                        <strong>Chicken:</strong> Once a week
                    </StatItem>
                    <StatItem>
                        <EggAltIcon sx={{ color: '#1e3a5f' }} />
                        <strong>Eggs:</strong> Twice a week
                    </StatItem>
                    <StatItem>
                        <CookieIcon sx={{ color: '#1e3a5f' }} />
                        <strong>Daily Raita or Dessert</strong>
                    </StatItem>
                </MealStats>
            </HeaderBox>

            {/* <SubHeader>
                <SubHeaderTitle>ELABORATE WEEKLY MENU</SubHeaderTitle>
                
            
                
                <NoteText>Non-Veg served at Dinner Only. 4 freshly cooked meals daily.</NoteText>
            </SubHeader> */}

            <TableContainer sx={{ overflowX: 'auto' }}>
                <StyledTable>
                    <TableHead>
                        <TableRow>
                            <DayCell>Day</DayCell>
                            {daysData.map((day) => (
                                <DayCell key={day.day}>{day.day}</DayCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {mealTypes.map((mealType) => (
                            <TableRow key={mealType}>
                                <MealTypeCell mealtype={mealType}>
                                    {mealType === 'BREAKFAST' && <WbSunnyIcon sx={{ mr: 1, fontSize: '16px' }} />}
                                    {mealType === 'DINNER' && <NightsStayIcon sx={{ mr: 1, fontSize: '16px' }} />}
                                    {mealType}
                                </MealTypeCell>
                                
                                {daysData.map((day) => {
                                    const items = day[mealType.toLowerCase()] || [];
                                    return (
                                        <TableCell key={`${day.day}-${mealType}`} sx={{ height: '130px' }}>
                                            <Box>
                                                {items.length > 0 ? (
                                                    items.map((item, idx) => (
                                                        <MenuItem key={idx}>
                                                            {getMealIcon(mealType, item)}
                                                            <FoodName>{item}</FoodName>
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem>
                                                        <RestaurantMenuIcon sx={{ color: '#ccc' }} />
                                                        <FoodName sx={{ color: '#ccc' }}>---</FoodName>
                                                    </MenuItem>
                                                )}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </StyledTable>
            </TableContainer>

            {/* <FooterNote>
                <BakeryDiningIcon sx={{ color: '#1e3a5f' }} />
                All meals are prepared fresh daily
                <span style={{ color: '#d4b483', margin: '0 5px' }}>✦</span>
                Menu subject to change based on seasonal availability
                <DinnerDiningIcon sx={{ color: '#1e3a5f' }} />
            </FooterNote>
            
            <FooterText>
                <RestaurantMenuIcon sx={{ fontSize: '14px', mr: 0.5 }} />
                Enjoy your meals!
                <RestaurantMenuIcon sx={{ fontSize: '14px', ml: 0.5 }} />
            </FooterText> */}
        </MenuContainer>
    );
};

export default WeeklyPrint;